import { container, singleton } from "tsyringe";

import { BehaviorSubject, interval, Subject } from 'rxjs';

import { BJNWebClientSDK, LoggingMode, Participant } from "@bluejeans/webrtc-client-sdk";

import { Config } from "../config";
import { defaultMeetingState, MeetingConnectionState, MeetingState, MeetingVideoState } from '../models/meetingClass';
import * as _ from 'lodash';
import { SEAMApiService } from './seamapi-service';
import { debounce } from 'rxjs/operators';


@singleton()
export class WebRTCsdkService {

  public devicePermissionError$ = new BehaviorSubject<any>(null);
  public localCameras$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public localMicrophones$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public localSpeakers$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  public selectedSpeaker$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public selectedCamera$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public selectedMicrophone$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  public meetingVideoState$: BehaviorSubject<MeetingVideoState> = new BehaviorSubject<any>(MeetingVideoState.INACTIVE_DISCONNECTED);
  public meetingState$: BehaviorSubject<MeetingState> = new BehaviorSubject<MeetingState>(defaultMeetingState);
  public participants$: BehaviorSubject<Participant[]> = new BehaviorSubject<Participant[]>([]);
  public seqparticipants$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  private currentSeqParticipants: any[] = [];
  private selfParticipant$ = new BehaviorSubject<Participant>(null);

  private sdkInitialized = false;
  private webrtcSDK: BJNWebClientSDK = null;
  private seamApiService = container.resolve(SEAMApiService);
  private isMeetingJoined = false;

  private localVideo: HTMLVideoElement = null; //  HTMLElement();
  public remoteVideos: HTMLVideoElement[] = null;  // array of HTMLElement();
  private contentVideo: HTMLVideoElement = null; // HTMLElement() for screen share

  public isBlurSupported = false;
  private lastBlurSettings = null;
  private _settingsCameraVisible = false;
  private _mySelfVideoMute = false;
  private _videoMuted = false;
  private virtualBlurQueue$ = new Subject();

  public videoAction$ = new BehaviorSubject<string>(null);
  public volumeLevel$ = new BehaviorSubject<number>(0);
  public virtualBlurAvailable$ = new BehaviorSubject<string>('');

  private assetsPathForTwilioModules = `${Config.DASHBOARD_URL}${process.env.PUBLIC_URL}/twilio-modules/`;

  set settingsCameraVisible(val) {
    console.log('WEBRTC settingsCameraVisible', val);
    this._settingsCameraVisible = val;
    this.checkForVirtualBlurBackgroundNeedsUpdate();
  }

  mySelfVideoMute(val) {
    console.log('WEBRTC mySelfVideoMute', val);
    this._mySelfVideoMute = val;
    this.checkForVirtualBlurBackgroundNeedsUpdate()
  }

  checkForVirtualBlurBackgroundNeedsUpdate() {
    const videoMuted = this._mySelfVideoMute && !this._settingsCameraVisible;
    if (this.lastBlurSettings && this.lastBlurSettings.videoMuted !== videoMuted) {
      this.virtualBlurQueue$.next(this.lastBlurSettings);
    }
  }

  get selfParticipantObservable() {
    return this.selfParticipant$.asObservable();
  }

  get sequinsEventObservable() {
    return this.seqparticipants$.asObservable();
  }

  public get participantsEventObservable() {
    return this.participants$.asObservable();
  }


  constructor() {
    if (!this.sdkInitialized) {
      this.sdkInitialized = true;
      this.initialize();
    }

    this.virtualBlurQueue$.pipe(debounce(() => interval(500)))
    .subscribe(async (values: any) => {
      try {
        await this.processVirtualBlurQueue(values)
      } catch(e) {
        console.log(e)
      }
    });
  }

  public attachLocalVideo = (ref) => {
    this.webrtcSDK.meetingService.attachLocalVideo(ref);
  }

  public attachRemoteVideo = (ref, index) => {
    this.webrtcSDK.meetingService.attachRemoteVideo(ref, index);
  }

  public attachRemoteContent = (ref) => {
    this.webrtcSDK.meetingService.attachRemoteContent(ref);
  }

  public handleVirtualBlurBackground(blurRange, isBlurEnabled, isVirtualBgEnabled) {

    this.virtualBlurQueue$.next(
      {
        blurRange,
        isBlurEnabled,
        isVirtualBgEnabled,
        videoMuted: null,
      }
    )
  }

  async processVirtualBlurQueue(values:  any) {

    if (!this.isBlurSupported) {
      await this.checkBackgroundLibrariesIsSupported();
      if (!this.isBlurSupported) {
        const res = (values.isBlurEnabled || values.isVirtualBgEnabled) ? 'notSupported' : 'ok';
        console.log('virtualBlurAvailable  is NOT supported ...', res);
        this.virtualBlurAvailable$.next(res);
        return;
      }
    }
    if (values) {

      this.lastBlurSettings = { ...values, videoMuted: this._mySelfVideoMute && !this._settingsCameraVisible };

      try {
        const lastUsedDevices = JSON.parse(localStorage.getItem('lastUsedDevices'));
        const lastUsedWebcam = lastUsedDevices.camera.id;

        const streamType = "local_video_stream";

        // blurRange : Number // Value to percentage to blur to be applied
        // isBlurEnabled: Boolean // if blur needs to be enabled
        // streamType: String // To get which stream needs to be updated with VB / Blur
        // lastUsedWebcam: String // To get the device info to capture stream
        // twilioModuleAssestsPath: String // To get path where all dependencies of Twillio are placed in build package
        // videoMute: Boolean // To get the state of remote mute state of video (localVideoStream)
        // isHairCheckScreen: Boolean // To not trigger localStreams changed
        // isVirtualEnabled: Boolean // If virtual background is enabled,
        // imageUrl: String // Image url that needs to be used as virtual background

        const blurParams = {
          blurRange: values.blurRange,
          isBlurEnabled: values.isBlurEnabled,
          streamType,
          lastUsedWebcam,
          twilioModuleAssestsPath: this.assetsPathForTwilioModules,  // keep Assests typo here or fix in WEBRTC-lib <!>
          videoMute: this._mySelfVideoMute && !this._settingsCameraVisible,
          isHairCheckScreen:  false,
          isVirtualEnabled : values.isVirtualBgEnabled,
          imageUrl: `${Config.DASHBOARD_URL}${process.env.PUBLIC_URL}/virtual-backgrounds/virtual-background-5.jpeg`
        };

        this.webrtcSDK.meetingService.handleVirtualBlurBackground(blurParams);
        this.virtualBlurAvailable$.next('ok');

      } catch (e) {
        console.log('Failed to fetch active camera', e);
        this.virtualBlurAvailable$.next('notSupported');
      }
    }
  }

  public async checkBackgroundLibrariesIsSupported() {
    const isSupported = await this.webrtcSDK.meetingService.checkBackgroundLibrariesIsSupported(this.assetsPathForTwilioModules);
    if (isSupported !== null) {
      this.isBlurSupported = isSupported;
      if (isSupported) {
        this.virtualBlurAvailable$.next('ok');
      }
    }
  }


  public getVolumeLevel = () => {
    return this.webrtcSDK.audioDeviceService.volumeLevel;
  }

  public setSelectedMicrophone = (id) => {
    const device = this.webrtcSDK.audioDeviceService.availableMicrophones.find((device) => { return device.id == id })
    if (device) {
      console.log("UI: audio input changed: " + device.name);
      this.webrtcSDK.audioDeviceService.selectMicrophone(device);

      let lastUsedDevices = JSON.parse(localStorage.getItem("lastUsedDevices") ?? "{}");
      lastUsedDevices = { ...lastUsedDevices, microphone: device };
      localStorage.setItem('lastUsedDevices', JSON.stringify(lastUsedDevices));

      setTimeout(() => {
        // check all good
        this.hasAllPermissions();
        // if something wrong devicePermissionError$ will be written with error
      }, 500);

    }
  }

  public setSelectedSpeaker = (id) => {
    const device = this.webrtcSDK.audioDeviceService.availableSpeakers.find((device) => { return device.id == id })
    if (device) {
      console.log("UI: audio output changed: " + device.name);
      this.webrtcSDK.audioDeviceService.selectSpeaker(device);

      let lastUsedDevices = JSON.parse(localStorage.getItem("lastUsedDevices") ?? "{}");
      lastUsedDevices = { ...lastUsedDevices, speaker: device };
      localStorage.setItem('lastUsedDevices', JSON.stringify(lastUsedDevices));

      setTimeout(() => {
        // check all good
        this.hasAllPermissions();
        // if something wrong devicePermissionError$ will be written with error
      }, 500);


    }
  }

  public setSelectedCamera = (id) => {
    const device = this.webrtcSDK.videoDeviceService.availableCameras.find((device) => { return device.id == id })
    if (device) {
      console.log("UI: video input changed: " + device.name);
      this.webrtcSDK.videoDeviceService.selectCamera(device);

      let lastUsedDevices = JSON.parse(localStorage.getItem("lastUsedDevices") ?? "{}");
      lastUsedDevices = { ...lastUsedDevices, camera: device };
      localStorage.setItem('lastUsedDevices', JSON.stringify(lastUsedDevices));

      setTimeout(() => {
        // check all good
        this.hasAllPermissions();
        // if something wrong devicePermissionError$ will be written with error
      }, 500);


    }
  }

  public setSelectedResolution = (id) => {
    console.log('UI: selected resolution changed: ', id);

    let lastUsedDevices = JSON.parse(localStorage.getItem("lastUsedDevices") ?? "{}");
    lastUsedDevices = { ...lastUsedDevices, resolution: id };
    localStorage.setItem('lastUsedDevices', JSON.stringify(lastUsedDevices));
  }

  public toggleAudioMute = () => {
    const muted = !this.webrtcSDK.meetingService.audioMuted;
    this.webrtcSDK.meetingService.setAudioMuted(muted);
  }

  public setAudioMuted = (muted) => {
    this.webrtcSDK.meetingService.setAudioMuted(muted);
  }

  public toggleVideoMute = () => {
    const muted = !this.webrtcSDK.meetingService.videoMuted;
    this.webrtcSDK.meetingService.setVideoMuted(muted);
  }

  public setVideoMuted = (muted) => {
    //this.webrtcSDK.meetingService.setVideoMuted(muted);
    this.webrtcSDK.meetingService.setVideoMuted(false);  // TODO test if local mute is enough

    // change the blur background -> video muted will stop blur or virtualbg
    this.mySelfVideoMute(muted);  // <!>  call the SET function
  }

  public transformSpeakerId = (deviceId, forwardTransform) => {
    const speakerSuffix = "_speaker";
    if (forwardTransform) {
      return deviceId + speakerSuffix;
    } else {
      if (deviceId && deviceId.indexOf(speakerSuffix) !== -1) {
        deviceId = deviceId.split(speakerSuffix)[0];
      }
      return deviceId;
    }
  }


  public startContentShare(): boolean {
    const isSupported = this.webrtcSDK.meetingService.contentService.isContentShareSupported;
    if (this.webrtcSDK.meetingService.contentService.isContentShareSupported) {
      this.webrtcSDK.meetingService.contentService.startContentShare();
      return true;
    } else {
      return false;
    }
  }

  public setVideoSharingAction(action) {
    console.log('GIC set videoSharingAction', action);
    this.videoAction$.next(action);
  }

  public stopContentShare(): boolean {
    if (this.webrtcSDK.meetingService.contentService.isContentShareSupported) {
      this.webrtcSDK.meetingService.contentService.stopContentShare();
      return true;
    } else {
      return false;
    }
  }

  public releasePresentationToken() {
    this.webrtcSDK.meetingService.contentService.releasePresentationToken();
  }


  public leaveMeeting = (): Promise<any> => {
    this.isMeetingJoined = false;
    const meetingState = this.meetingState$.getValue();

    if (meetingState.connexionState === MeetingConnectionState.CONNECTED) {
      return this.webrtcSDK.meetingService.endMeeting();
    }

    return null;
  }

  public joinMeeting = async (id, passcode, name) => {

    if (this.isMeetingJoined) {
      return;
    }
    this.isMeetingJoined = true;
    try {
      await this.webrtcSDK.meetingService.joinMeeting(id, passcode, name);
      // all good => clear any previous error
      this.devicePermissionError$.next(null);

      console.log('TOKEN bjn meeting', this.seamApiService?.meetingToken);
    } catch (error) {
      console.log('Join meeting failed', error);
      this.devicePermissionError$.next({ code: 'UNABLE_TO_JOIN_MEETING', reason: "Unable to join the Event Meeting!" });
    }
  }

  private updateCameraList = () => {
    this.localCameras$.next(this.webrtcSDK.videoDeviceService.availableCameras);
  }

  private updateMicrophoneList = () => {
    this.localMicrophones$.next(this.webrtcSDK.audioDeviceService.availableMicrophones);
  }

  private updateSpeakerList = () => {
    this.localSpeakers$.next(this.webrtcSDK.audioDeviceService.availableSpeakers);
  }

  private updateSelectedSpeaker = () => {
    this.selectedSpeaker$.next(this.webrtcSDK.audioDeviceService.selectedSpeaker);
  }

  private updateSelectedMicrophone = () => {
    console.log('SELECTED microphone', this.webrtcSDK.audioDeviceService.selectedMicrophone);
    this.selectedMicrophone$.next(this.webrtcSDK.audioDeviceService.selectedMicrophone);
  }

  private updateSelectedCamera = () => {
    console.log('SELECTED camera', this.webrtcSDK.videoDeviceService.selectedCamera)
    this.selectedCamera$.next(this.webrtcSDK.videoDeviceService.selectedCamera);
  }

  private updateRemoteVideoMask = () => {
    this.meetingVideoState$.next(this.webrtcSDK.meetingService.videoState.toString() as MeetingVideoState);
    // switch(videoState) {
    //   case this.webrtcSDK.VideoState.INACTIVE_NEEDS_MODERATOR: // no one in mtg
    //   this.videoState$.next(this.mee);
    //   //   $("#remoteVideo0").hide();
    //   //   $("#remoteVideo1").hide();
    //   //   $("#remoteVideo2").hide();
    //   //   $("#waitimg").hide();
    //   //   $("#waittext").hide();
    //   //   $("#wait4agent").show();
    //   break;
    //   case this.webrtcSDK.VideoState.INACTIVE_ONLY_PARTICIPANT: // just me
    //     $("#remoteVideo0").hide();
    //     $("#remoteVideo1").hide();
    //     $("#remoteVideo2").hide();
    //     $("#wait4agent").show();
    //     $("#waitimg").css('display','block').show();
    //     $("#waittext").show();
    //   break;
    //   default: // everyone
    //     $("#remoteVideo0").show();
    //     $("#remoteVideo1").show();
    //     $("#remoteVideo2").show();
    //     $("#waitimg").hide();
    //     $("#waittext").hide();
    //     $("#wait4agent").hide();
    //   break;
    // }
  }

  private updateAudioMuteButton = () => {
    const audioMute = this.webrtcSDK.meetingService.audioMuted;
    const currentState = this.meetingState$.getValue();
    const newState = { ...currentState, audioMute };
    console.log("update audioMute", audioMute ? "Audio is Muted now" : "Audio is Unmuted now");
    this.meetingState$.next(newState);
  }

  private updateVideoMuteButton = () => {
    const videoMute = this.webrtcSDK.meetingService.videoMuted;
    const currentState = this.meetingState$.getValue();
    const newState = { ...currentState, videoMute };
    console.log("update videoMute", videoMute ? "Video is Muted now" : "Video is Unmuted now");
    this.meetingState$.next(newState);
  }

  private updateRoster = () => {
    const participants = this.webrtcSDK.meetingService.participantService.participants;
    console.log("update roster", participants);
    this.participants$.next(participants);
  }

  private enqueueSeqParticipants = () => {
    this.seqparticipants$.next(this.currentSeqParticipants);
  }

  private updateSequinParticipants = () => {
    const seqparticipants = this.webrtcSDK.meetingService.sequinParticipantService.sequinParticipants;


    const activeStreams = this.webrtcSDK.meetingService.sequinParticipantService.activeStreams.filter((p: any) => !isNaN(p.csrc));

    if (this.participantsHasChanged(this.currentSeqParticipants, seqparticipants)) {
      this.currentSeqParticipants = seqparticipants;
      console.log('GIC sequin participants', seqparticipants);
      console.log('GIC sequin active', activeStreams);
      console.log('GIC debounce write', Date.now());
      _.debounce(this.enqueueSeqParticipants, 1000)();
    }
  }

  participantsHasChanged(seq1, seq2) {
    if (seq1.length !== seq2.length) return true;
    for (let index = 0; index < seq1.length; index++) {
      const p1 = seq1[index];
      let p2 = seq2.find(s => s.csrc === p1.csrc);
      if (!p2) {
        if (p1.participant && p1.participant.participantGuid) {
          // check on participant.guid
          p2 = seq2.find(s => s.participant?.participantGuid === p1.participant.participantGuid);
        }
      }
      if (!p2) {
        return true;
      }
      if (p1.streamIndex !== p2.streamIndex) return true;
      if (p1.participant?.participantGuid !== p2.participant?.participantGuid) return true;
      if (p1.name !== p2.name) return true;
      if (p1.isAudioOnly !== p2.isAudioOnly) return true;
      if (p1.callQuality !== p2.callQuality) return true;
    }

    return false;
  }

  private updateReceivingContent = () => {
    const receivingContent = this.webrtcSDK.meetingService.contentService.receivingContentShare;
    const currentState = this.meetingState$.getValue();
    const newState = { ...currentState, receivingContent };
    console.log("update roster", 'Receiving remote content ' + (receivingContent ? 'starts' : 'stops'));
    this.meetingState$.next(newState);
  }

  private updateContentShareState = () => {
    const sharingLocalContent = this.webrtcSDK.meetingService.contentService.contentShareState;
    console.log('GIC content share state', sharingLocalContent);

    const currentState = this.meetingState$.getValue();
    const newState = { ...currentState, sharingLocalContent };
    this.meetingState$.next(newState);
  }

  private updateConnectionState = () => {
    const connexionState = this.webrtcSDK.meetingService.connectionState.toString() as MeetingConnectionState;
    const currentState = this.meetingState$.getValue();
    const newState = { ...currentState, connexionState };
    console.log("update connexionState", connexionState);
    this.meetingState$.next(newState);
  }

  private updateSelfParticipant = () => {
    this.selfParticipant$.next(this.webrtcSDK.meetingService.participantService.selfParticipant);
  }

  initialize() {

    this.webrtcSDK = new BJNWebClientSDK();
    this.webrtcSDK.loggingService.setLoggingMode(LoggingMode.DEBUG);

    this.webrtcSDK.meetingService.setCluster(Config.WEBRTC_CLUSTER_CONFIG);
    // Observe SDK values
    this.webrtcSDK.audioDeviceService.observe("availableMicrophones", this.updateMicrophoneList);
    this.webrtcSDK.audioDeviceService.observe("availableSpeakers", this.updateSpeakerList);
    this.webrtcSDK.videoDeviceService.observe("availableCameras", this.updateCameraList);

    this.webrtcSDK.meetingService.observe("videoState", this.updateRemoteVideoMask);
    this.webrtcSDK.meetingService.observe("audioMuted", this.updateAudioMuteButton);
    this.webrtcSDK.meetingService.observe("videoMuted", this.updateVideoMuteButton);
    this.webrtcSDK.meetingService.participantService.observe("participants", this.updateRoster);
    this.webrtcSDK.meetingService.participantService.observe("selfParticipant", this.updateSelfParticipant);

    this.webrtcSDK.meetingService.sequinParticipantService.observe("sequinParticipants", this.updateSequinParticipants);
    this.webrtcSDK.meetingService.contentService.observe("receivingContentShare", this.updateReceivingContent);
    this.webrtcSDK.meetingService.contentService.observe("contentShareState", this.updateContentShareState);

    this.webrtcSDK.meetingService.observe("connectionState", this.updateConnectionState);

    this.webrtcSDK.audioDeviceService.observe("selectedMicrophone", this.updateSelectedMicrophone);
    this.webrtcSDK.audioDeviceService.observe("selectedSpeaker", this.updateSelectedSpeaker);
    this.webrtcSDK.videoDeviceService.observe("selectedCamera", this.updateSelectedCamera);

    // start polling volumeLevel
    setInterval(() => {
      this.volumeLevel$.next(this.getVolumeLevel());
    }, 333)

  }

  askPermissions() {

    // Ask for permissions up front so that full device names are shown
    this.hasAllPermissions();

  }

  hasAllPermissions() {

    this.webrtcSDK.permissionService.requestAllPermissions().then((granted) => {
      if (granted) {
        console.log("Permissions granted!")
        this.devicePermissionError$.next(null);
      } else {
        this.devicePermissionError$.next({ code: 'CAM_IN_USE', reason: "Camera is invalid" });
      }
    }, (error) => {
      console.log("Local device error " + error);
      this.devicePermissionError$.next(error);
    })
  }

}
